// emitSource — UTF-16 code-unit patch engine (core).
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: emitSource — byte-range patch engine (core).
//
// Source-preserving UTF-16 code-unit patching. Applies `ModifiedStatement.plannedEdits`
// to `originalText` right-to-left (avoids offset drift) and preserves every code unit
// in untouched regions exactly. No AST re-printing, no whitespace normalization,
// no quoting style rewriting.
//
// Edge-case behavior (comments / whitespace / quoting / line-endings) lives in
// the sibling `emitSource.edges.ts` module. This file owns the splicer, the
// non-overlap validator, the comment-overlap validator, and EOL normalisation.

import type { SqlEngine, SqlRange } from '../../sql-grammar/model';

import type { PlannedByteEdit } from '../types';

/** Structural input to `emitSource` — accepts any object that exposes a
 *  `plannedEdits` array. Both `ModifiedStatement` (SR-Edit, SELECT/DML) and
 *  `ModifiedDdlStatement` (DDL Structural Edit) satisfy this shape, so the
 *  UTF-16 code-unit patcher can serve both edit families unchanged (DSE1). */
export type EmitSourceInput = { readonly plannedEdits: readonly PlannedByteEdit[] };

import {
  detectLineEnding,
  findEnclosingComment,
  normalizeLineEndings,
} from './emitSource.edges';

export type EmitSourceErrorCode =
  | 'overlapping-edits'
  | 'edit-out-of-bounds'
  | 'inverted-range'
  | 'edit-inside-comment';

export interface EmitSourceOptions {
  // When true, skip the EOL normalisation pass and leave each replacement's
  // line endings untouched. Defaults to false (normalisation enabled).
  readonly skipEolNormalize?: boolean;
  // Source engine. Only used to decide whether `#` opens a line comment
  // (MySQL-only). When undefined or not 'mysql', `#` is treated as a normal
  // character (e.g. MSSQL temp-table names `#tmp` / `##global`) — the correct
  // safe default for the non-MySQL majority.
  readonly engine?: SqlEngine;
}

export class EmitSourceError extends Error {
  readonly code: EmitSourceErrorCode;
  readonly edit?: PlannedByteEdit;
  readonly other?: PlannedByteEdit;
  constructor(code: EmitSourceErrorCode, message: string, edit?: PlannedByteEdit, other?: PlannedByteEdit) {
    super(message);
    this.name = 'EmitSourceError';
    this.code = code;
    this.edit = edit;
    this.other = other;
  }
}

export function emitSource(
  modified: EmitSourceInput,
  originalText: string,
  options: EmitSourceOptions = {},
): string {
  const edits = modified.plannedEdits;
  if (edits.length === 0) return originalText;

  for (const e of edits) {
    if (!isValidBounds(e.range, originalText.length)) {
      throw new EmitSourceError(
        'edit-out-of-bounds',
        `emitSource: edit range [${e.range.startOffset}, ${e.range.endOffset}) is out of bounds for source length ${originalText.length}`,
        e,
      );
    }
    if (e.range.endOffset < e.range.startOffset) {
      throw new EmitSourceError(
        'inverted-range',
        `emitSource: edit range [${e.range.startOffset}, ${e.range.endOffset}) is inverted`,
        e,
      );
    }
  }

  assertEditsNotInsideComments(originalText, edits, options.engine);

  // Sort descending by startOffset, then descending by endOffset (longer-range
  // first when starts coincide). This makes overlap detection deterministic
  // and ensures right-to-left application places a zero-width insert AFTER
  // an adjacent replacement that begins at the same offset.
  const sorted = [...edits].sort((a, b) => {
    const ds = b.range.startOffset - a.range.startOffset;
    if (ds !== 0) return ds;
    return b.range.endOffset - a.range.endOffset;
  });
  assertNonOverlapping(sorted);

  const eol = options.skipEolNormalize ? null : detectLineEnding(originalText);

  let out = originalText;
  for (const e of sorted) {
    const replacement = eol === null ? e.replacement : normalizeLineEndings(e.replacement, eol);
    // String.prototype.slice uses UTF-16 code-unit offsets — consistent with SqlRange.
    out = out.slice(0, e.range.startOffset) + replacement + out.slice(e.range.endOffset);
  }
  return out;
}

// An edit whose start offset sits strictly inside a `/* */`, `--`, or (MySQL)
// `#` comment is almost certainly an applicator bug (the planner should have
// flagged the comment when computing offsets). emitSource refuses to emit so
// the caller gets a loud failure instead of corrupted SQL. Boundary-aligned
// edits (insert AT comment start, replace covering the full comment, insert
// AT comment end) are allowed — `findEnclosingComment` returns null at the
// boundaries by design. `engine` gates `#`-as-comment to MySQL only.
function assertEditsNotInsideComments(source: string, edits: readonly PlannedByteEdit[], engine?: SqlEngine): void {
  for (const e of edits) {
    const region = findEnclosingComment(source, e.range.startOffset, engine);
    if (!region) continue;
    throw new EmitSourceError(
      'edit-inside-comment',
      `emitSource: edit range [${e.range.startOffset}, ${e.range.endOffset}) starts inside a ${region.kind} comment at [${region.start}, ${region.end}); applicator must split or skip it`,
      e,
    );
  }
}

function isValidBounds(range: SqlRange, length: number): boolean {
  return range.startOffset >= 0 && range.endOffset <= length;
}

// Sorted descending by startOffset. Two edits overlap when one's range
// intersects the other. Adjacency (a.end === b.start) is allowed.
// Two zero-width inserts at the same offset overlap-as-ambiguous: emit
// rejects this since the applicator is required to merge them itself.
function assertNonOverlapping(sortedDesc: readonly PlannedByteEdit[]): void {
  for (let i = 0; i + 1 < sortedDesc.length; i++) {
    const higher = sortedDesc[i]!;
    const lower = sortedDesc[i + 1]!;
    // sortedDesc means higher.start >= lower.start
    if (lower.range.endOffset > higher.range.startOffset) {
      throw new EmitSourceError(
        'overlapping-edits',
        `emitSource: planned edits overlap — [${lower.range.startOffset}, ${lower.range.endOffset}) and [${higher.range.startOffset}, ${higher.range.endOffset})`,
        lower,
        higher,
      );
    }
    if (lower.range.endOffset === higher.range.startOffset
        && lower.range.startOffset === lower.range.endOffset
        && higher.range.startOffset === higher.range.endOffset) {
      throw new EmitSourceError(
        'overlapping-edits',
        `emitSource: two zero-width inserts share offset ${lower.range.startOffset}; merge them in the applicator`,
        lower,
        higher,
      );
    }
  }
}
